import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { TownEyeLogo } from "@/components/TownEyeLogo";
import { PasswordField } from "@/components/PasswordField";
import { Loader2, Mail, Phone, ShieldCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

export default function LoginPage() {
  const { login, googleLogin, phoneRequestOtp, phoneVerifyOtp } = useAuth();
  const { language } = useI18n();
  const navigate = useNavigate();
  const loc = useLocation();
  const redirectTo = loc.state?.from || "/dashboard";
  const [method, setMethod] = useState("email");

  const labels = {
    welcome: { ar: "أهلاً بعودتك", he: "ברוך שובך", en: "Welcome back" },
    sub: { ar: "سجِّل دخولك إلى حساب TownEye.", he: "התחבר לחשבון TownEye שלך.", en: "Sign in to your TownEye account." },
    email: { ar: "البريد", he: "אימייל", en: "Email" },
    phone: { ar: "الهاتف", he: "טלפון", en: "Phone" },
    new: { ar: "جديد على TownEye؟", he: "חדש ב-TownEye?", en: "New to TownEye?" },
    create: { ar: "أنشئ حساباً", he: "צור חשבון", en: "Create an account" },
  };
  const L = (k) => labels[k][language] || labels[k].en;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8 bg-[var(--te-bg-soft)]">
      <div className="w-full max-w-md">
        <Link to="/" className="flex justify-center mb-6" data-testid="login-logo-link">
          <TownEyeLogo size={26} />
        </Link>

        <div
          className="bg-white rounded-[28px] border border-[var(--te-divider)] p-6 sm:p-8 te-fade-up"
          style={{ boxShadow: "0 24px 60px -20px rgba(103, 58, 183,0.15)" }}
        >
          <h1 className="font-extrabold text-3xl text-[var(--te-text)] text-center">
            {L("welcome")}
          </h1>
          <p className="text-sm text-[var(--te-text-secondary)] mt-2 text-center">{L("sub")}</p>

          <div className="mt-7 grid grid-cols-2 gap-1 bg-[var(--te-input-bg)] p-1 rounded-full">
            <button
              type="button"
              onClick={() => setMethod("email")}
              data-testid="login-tab-email"
              className={`text-sm font-semibold py-2 rounded-full flex items-center justify-center gap-1.5 transition-all ${
                method === "email" ? "bg-white text-[var(--te-primary)] shadow-sm" : "text-[var(--te-text-secondary)]"
              }`}
            >
              <Mail size={14} /> {L("email")}
            </button>
            <button
              type="button"
              onClick={() => setMethod("phone")}
              data-testid="login-tab-phone"
              className={`text-sm font-semibold py-2 rounded-full flex items-center justify-center gap-1.5 transition-all ${
                method === "phone" ? "bg-white text-[var(--te-primary)] shadow-sm" : "text-[var(--te-text-secondary)]"
              }`}
            >
              <Phone size={14} /> {L("phone")}
            </button>
          </div>

          {method === "email"
            ? <EmailLogin login={login} googleLogin={googleLogin} language={language}
                onDone={(user) => navigate(user?.onboarded === false ? "/onboarding" : redirectTo)} />
            : <PhoneLogin phoneRequestOtp={phoneRequestOtp} phoneVerifyOtp={phoneVerifyOtp} language={language}
                onDone={(user) => navigate(user?.onboarded === false ? "/onboarding" : redirectTo)} />
          }

          <p className="mt-6 text-center text-sm text-[var(--te-text-secondary)]">
            {L("new")}{" "}
            <Link to="/register" className="font-bold text-[var(--te-primary)] hover:underline" data-testid="login-link-register">
              {L("create")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function EmailLogin({ login, googleLogin, language, onDone }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const isAr = language === "ar"; const isHe = language === "he";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(isAr ? "أهلاً بعودتك!" : isHe ? "ברוך שובך!" : "Welcome back!");
      onDone(u);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const doGoogle = async () => {
    setBusy(true);
    try {
      const demoEmail = window.prompt(isAr ? "تابع بـ Google — أدخل بريد Google" : isHe ? "המשך עם Google — הזן אימייל" : "Continue with Google — enter your Google email", "");
      if (!demoEmail) { setBusy(false); return; }
      const demoName = window.prompt(isAr ? "الاسم" : isHe ? "שם" : "Display name", demoEmail.split("@")[0]) || demoEmail.split("@")[0];
      const u = await googleLogin({ email: demoEmail, name: demoName });
      toast.success(isAr ? "تم الدخول عبر Google" : isHe ? "התחברת עם Google" : "Signed in with Google");
      onDone(u);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button
        type="button"
        onClick={doGoogle}
        disabled={busy}
        data-testid="login-google-btn"
        className="mt-6 w-full flex items-center justify-center gap-3 border border-[var(--te-divider)] bg-white rounded-full py-3 px-6 hover:bg-[var(--te-bg-soft)] transition-colors font-semibold text-sm"
      >
        <GoogleIcon />
        <span>{isAr ? "تابع باستخدام Google" : isHe ? "המשך עם Google" : "Continue with Google"}</span>
      </button>

      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-[var(--te-divider)]" />
        <span className="text-xs uppercase tracking-wider text-[var(--te-text-muted)]">{isAr ? "أو" : isHe ? "או" : "or"}</span>
        <div className="flex-1 h-px bg-[var(--te-divider)]" />
      </div>

      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="te-label">{isAr ? "البريد" : isHe ? "אימייל" : "Email"}</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="te-input"
            placeholder="you@example.com"
            data-testid="login-email-input"
            autoComplete="email"
            dir="ltr"
          />
        </div>
        <PasswordField
          value={password}
          onChange={setPassword}
          showStrength={false}
          testId="login-password-input"
          autoComplete="current-password"
          minLength={1}
          placeholder={isAr ? "كلمة المرور" : isHe ? "סיסמה" : "Your password"}
          label={isAr ? "كلمة المرور" : isHe ? "סיסמה" : "Password"}
        />
        <button
          type="submit"
          disabled={busy || !email || !password}
          data-testid="login-submit-btn"
          className="te-btn-primary w-full !py-3.5"
        >
          {busy ? <><Loader2 size={16} className="animate-spin" /> {isAr ? "جارٍ الدخول…" : isHe ? "מתחבר…" : "Signing in…"}</>
                : (isAr ? "تسجيل الدخول" : isHe ? "התחבר" : "Sign in")}
        </button>
      </form>
    </>
  );
}

function PhoneLogin({ phoneRequestOtp, phoneVerifyOtp, language, onDone }) {
  const [stage, setStage] = useState("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const phoneValid = /^\+?[0-9\s\-()]{7,18}$/.test(phone);
  const isAr = language === "ar"; const isHe = language === "he";

  const requestOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await phoneRequestOtp(phone.trim());
      toast.success(data.message || "OTP sent");
      if (data.dev_code) toast(`Demo OTP: ${data.dev_code}`, { duration: 8000 });
      setStage("otp");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await phoneVerifyOtp({ phone: phone.trim(), code: otp.trim() });
      toast.success(isAr ? "تم الدخول" : isHe ? "התחברת" : "Signed in");
      onDone(u);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  if (stage === "phone") {
    return (
      <form onSubmit={requestOtp} className="mt-6 space-y-4">
        <div>
          <label className="te-label">{isAr ? "رقم الهاتف" : isHe ? "מספר טלפון" : "Phone number"}</label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+972 50 123 4567"
            className="te-input"
            data-testid="login-phone-input"
            autoComplete="tel"
            dir="ltr"
          />
        </div>
        <button
          type="submit"
          disabled={!phoneValid || busy}
          data-testid="login-phone-send-otp-btn"
          className="te-btn-primary w-full !py-3.5"
        >
          {busy ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : (isAr ? "إرسال رمز التحقق" : isHe ? "שלח קוד אימות" : "Send verification code")}
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={verifyOtp} className="mt-6 space-y-4">
      <button type="button" onClick={() => setStage("phone")} className="text-xs text-[var(--te-text-secondary)] flex items-center gap-1 hover:text-[var(--te-text)]">
        <ArrowLeft size={12} /> {isAr ? "استخدم رقماً آخر" : isHe ? "השתמש במספר אחר" : "Use a different number"}
      </button>
      <div className="flex items-center gap-2 text-sm text-[var(--te-text)] bg-[var(--te-primary-soft)] rounded-xl px-4 py-3">
        <ShieldCheck size={16} className="text-[var(--te-primary)]" />
        <span>{isAr ? "تم إرسال الرمز إلى" : isHe ? "הקוד נשלח אל" : "Code sent to"} <span className="font-bold" dir="ltr">{phone}</span></span>
      </div>
      <div>
        <label className="te-label">{isAr ? "رمز مكوّن من 6 أرقام" : isHe ? "קוד בן 6 ספרות" : "6-digit code"}</label>
        <input
          inputMode="numeric"
          maxLength={6}
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ""))}
          placeholder="123456"
          className="te-input text-center text-lg tracking-widest"
          data-testid="login-phone-otp-input"
          dir="ltr"
        />
      </div>
      <button
        type="submit"
        disabled={otp.length < 4 || busy}
        data-testid="login-phone-verify-btn"
        className="te-btn-primary w-full !py-3.5"
      >
        {busy ? <><Loader2 size={16} className="animate-spin" /> Verifying…</> : (isAr ? "تحقق وادخل" : isHe ? "אמת והתחבר" : "Verify & sign in")}
      </button>
    </form>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 5.1 29.3 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21 21-9.4 21-21c0-1.2-.1-2.4-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 5.1 29.3 3 24 3 16.3 3 9.7 7.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 45c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.9 26.7 37 24 37c-5.3 0-9.7-3.4-11.3-8L6 33.7C9.3 40.6 16.1 45 24 45z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.3-4.1 5.6l6.2 5.2C41.9 35.4 45 30.1 45 24c0-1.2-.1-2.4-.4-3.5z"/>
    </svg>
  );
}
